import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAssociation } from '~/composables/useAssociation';
import { useEnvPropsStore } from '~/stores/envProps';
import { useBaseStore } from '~/stores/base';
import { useQuickViewStore } from '~/stores/quickView';
import { decrypt } from '~/utils/CryptoUtil';
import { SET_ATB_COMPONENT, SET_ATB_LOADING_STATE } from '~/types/mutations';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import useAddToBag from '~/composables/useAddToBag';

export const useGiftCard = ({ propSlideoutId, propProduct, propIsRegistrant, eGiftEnabled, emits }) => {
  const envPropsStore = useEnvPropsStore();
  const baseStore = useBaseStore();
  const quickViewStore = useQuickViewStore();
  const slideoutStore = useSlideoutStore();
  const { addItemToBag } = useAddToBag();

  const { registryId } = useAssociation();
  const route = useRoute();

  const orgId = computed(() => (envPropsStore.envProps.entryPoint?.toUpperCase() === 'MCOM' ? 12 : 13));
  const giftCardTitle = computed(() => (orgId.value === 12 ? "MACY's" : "BLOOMINGDALE'S"));
  const regId = computed(() => {
    if (propIsRegistrant) {
      return registryId.value;
    }
    return route?.params?.registryId;
  });
  const giftCardEnabled = computed(() => baseStore.giftCardEnabled);
  const eGiftCardEnabled = computed(() => baseStore.eGiftCardEnabled);
  const giftCardAmount = computed(() => quickViewStore.giftCardAmount);
  const inValidGiftCardAmount = computed(() => quickViewStore.inValidGiftCardAmount);
  const giftCardMessage = computed(() => quickViewStore.giftCardMessage);
  const deliveryOption = computed(() => quickViewStore.giftCardDeliveryOption?.toUpperCase());
  const secretKey = computed(() => envPropsStore.envProps?.secretKey);
  const upcInfo = computed(() => {
    let upcId = propProduct?.detail?.upcId;
    let upcNumber = propProduct?.detail?.upcNumber;
    if (eGiftEnabled && eGiftCardEnabled.value && deliveryOption.value === 'E-MAIL' && propProduct?.eUpcId && propProduct?.eUpcNumber) {
      upcId = propProduct.eUpcId;
      upcNumber = propProduct.eUpcNumber;
    }
    return { upcId, upcNumber };
  });
  const isAddToBagLoading = computed(() => quickViewStore.quickViewLoadingProductId === propProduct.id);

  const handleAddToBag = async () => {
    quickViewStore[SET_ATB_LOADING_STATE]({ source: 'QUICKVIEW', productId: propProduct.id });

    const giftCardEmailId = decrypt(propProduct.egiftCardId, secretKey.value);
    const payload = {
      item: {
        quantity: 1,
        giftUPCID: upcInfo.value?.upcId?.toString(),
        upcId: upcInfo.value?.upcId?.toString(),
        upcNumber: upcInfo.value?.upcNumber?.toString(),
        registryId: regId.value,
        giftCardAmount: giftCardAmount.value.startsWith('$') ? giftCardAmount.value.slice(1) : giftCardAmount.value,
        giftCardMessage: giftCardMessage.value,
        source: 'REGA2B',
        ...(eGiftEnabled && eGiftCardEnabled.value && deliveryOption.value === 'E-MAIL' && giftCardEmailId && { giftCardEmailId }),
      },
    };

    return addItemToBag(payload)
      .then((atbConfirmation) => {
        slideoutStore.toggle(propSlideoutId, { root: true });
        quickViewStore[SET_ATB_COMPONENT](atbConfirmation.component);
      })
      .catch((error) => {
        const message = error?.cause?.message || error?.message;

        emits('registry-atb-error', message);
      })
      .finally(() => {
        quickViewStore[SET_ATB_LOADING_STATE]({ source: 'QUICKVIEW', productId: null });
      });
  };

  return {
    giftCardTitle,
    giftCardEnabled,
    eGiftCardEnabled,
    handleAddToBag,
    regId,
    registryId,
    giftCardAmount,
    inValidGiftCardAmount,
    deliveryOption,
    secretKey,
    orgId,
    giftCardMessage,
    upcInfo,
    isAddToBagLoading,
  };
};
